import { useEffect, useState } from 'react'
import { AlertTriangle, CalendarDays, Merge, Search, X } from 'lucide-react'
import { cmsApi } from '../lib/api'
import { cardImagePosition, statusLabel } from '../lib/format'
import type { EventSummary, MergeResult } from '../types'
import { EventSummaryLine } from './SimilarEvents'

type Props = {
  keepId: string
  keepTitle: string
  catalogEditing: boolean
  onClose: () => void
  onMerged: (result: MergeResult, mergedTitle: string) => void
  onError: (message: string) => void
}

// Until the cutover the sheet's sync derives events from title and category
// and would bring a joined duplicate back.
export function SheetSyncNotice({ catalogEditing }: { catalogEditing: boolean }) {
  if (catalogEditing) return null
  return (
    <div className="notice" role="note">
      <AlertTriangle size={18} />
      <span>A planilha ainda sincroniza eventos: uma junção feita agora pode ser desfeita na próxima sincronização. Junte depois da virada para o Guve 2.0.</span>
    </div>
  )
}

// Joins a duplicate into the event being edited, which stays: its places,
// sessions, favorites and address come here (cms_merge_events).
export function MergeDialog({ keepId, keepTitle, catalogEditing, onClose, onMerged, onError }: Props) {
  const [query, setQuery] = useState(keepTitle)
  const [items, setItems] = useState<EventSummary[]>([])
  const [selected, setSelected] = useState<EventSummary | null>(null)
  const [merging, setMerging] = useState(false)

  useEffect(() => {
    const words = query.trim()
    if (words.length < 3) { setItems([]); return }
    let current = true
    const timer = window.setTimeout(() => {
      cmsApi.similarEvents(words, keepId)
        .then((result) => { if (current) setItems(result) })
        .catch(() => { if (current) setItems([]) })
    }, 300)
    return () => { current = false; window.clearTimeout(timer) }
  }, [query, keepId])

  useEffect(() => {
    const close = (event: KeyboardEvent) => { if (event.key === 'Escape' && !merging) onClose() }
    window.addEventListener('keydown', close)
    return () => window.removeEventListener('keydown', close)
  }, [merging, onClose])

  const merge = async () => {
    if (!selected) return
    setMerging(true)
    try {
      onMerged(await cmsApi.mergeEvents(keepId, selected.id), selected.title)
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Não foi possível juntar os eventos.')
      setMerging(false)
    }
  }

  return (
    <div className="dialog-backdrop" role="presentation" onClick={() => !merging && onClose()}>
      <section className="dialog" role="dialog" aria-modal="true" aria-labelledby="merge-title" onClick={(event) => event.stopPropagation()}>
        <div className="dialog__heading">
          <div>
            <h2 id="merge-title">Juntar outro evento a este</h2>
            <p>O evento escolhido entra em “{keepTitle}”, que fica. Locais, horários, favoritos e o endereço dele passam para cá, e ele deixa de existir.</p>
          </div>
          <button type="button" className="icon-button" aria-label="Fechar" disabled={merging} onClick={onClose}><X size={18} /></button>
        </div>

        <SheetSyncNotice catalogEditing={catalogEditing} />

        <label className="search-field">
          <Search size={18} />
          <input aria-label="Buscar evento para juntar" value={query} onChange={(event) => { setQuery(event.target.value); setSelected(null) }} placeholder="Buscar pelo título" />
        </label>

        <div className="dialog__list" role="radiogroup" aria-label="Evento que entra neste">
          {items.map((item) => (
            <label key={item.id} className={`merge-option ${selected?.id === item.id ? 'merge-option--selected' : ''}`}>
              <input type="radio" name="merged" aria-label={item.title} checked={selected?.id === item.id} onChange={() => setSelected(item)} />
              <span className="compact-event__media">{item.image_url ? <img src={item.image_url} alt="" style={{ objectPosition: cardImagePosition(item) }} /> : <CalendarDays size={18} />}</span>
              <span className="similar-event__main"><strong>{item.title}</strong><EventSummaryLine event={item} /></span>
              <span className={`status-pill status-pill--${item.status}`}>{statusLabel(item.status)}</span>
            </label>
          ))}
          {!items.length && <div className="empty-inline">{query.trim().length < 3 ? 'Digite o título do evento repetido.' : 'Nenhum evento parecido.'}</div>}
        </div>

        {selected && (
          <div className="merge-summary">
            <strong>Vai entrar neste evento</strong>
            <span>{selected.places || 'Sem local'} · {selected.sessions} {selected.sessions === 1 ? 'horário' : 'horários'} · {selected.saves} {selected.saves === 1 ? 'favorito' : 'favoritos'}</span>
            <span>Horários iguais no mesmo local viram um só. O endereço de “{selected.title}” passa a abrir este evento.</span>
          </div>
        )}

        <div className="dialog__actions">
          <button type="button" className="button button--secondary" disabled={merging} onClick={onClose}>Cancelar</button>
          <button type="button" className="button button--primary" disabled={!selected || merging} onClick={() => void merge()}>
            <Merge size={17} /> {merging ? 'Juntando…' : 'Juntar eventos'}
          </button>
        </div>
      </section>
    </div>
  )
}
